import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'
import { cn } from '@/lib/utils'

export const textVariants = cva('', {
  variants: {
    variant: {
      'heading-xl': 'font-bold text-4xl leading-[1.1] tracking-tight md:text-5xl',
      'heading-lg': 'font-bold text-3xl leading-[1.15] tracking-tight md:text-4xl',
      'heading-md': 'font-semibold text-2xl leading-[1.2] tracking-tight md:text-3xl',
      'heading-sm': 'font-semibold text-xl leading-snug tracking-normal md:text-2xl',
      'body-lg': 'text-lg leading-relaxed',
      'body-md': 'text-base leading-relaxed',
      'body-sm': 'text-sm leading-relaxed',
      'label-lg': 'font-medium text-base leading-snug',
      'label-md': 'font-medium text-sm leading-snug',
      'label-sm': 'font-medium text-xs leading-snug',
      lead: 'text-lg text-muted-foreground leading-relaxed',
      caption: 'text-muted-foreground text-sm',
      code: 'rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm',
    },
  },
  defaultVariants: {
    variant: 'body-md',
  },
})

const variantElementMap = {
  'heading-xl': 'h1',
  'heading-lg': 'h2',
  'heading-md': 'h3',
  'heading-sm': 'h4',
  'body-lg': 'p',
  'body-md': 'p',
  'body-sm': 'p',
  'label-lg': 'label',
  'label-md': 'label',
  'label-sm': 'label',
  lead: 'p',
  caption: 'span',
  code: 'code',
} as const

const elementVariantMap = {
  h1: 'heading-xl',
  h2: 'heading-lg',
  h3: 'heading-md',
  h4: 'heading-sm',
  h5: 'heading-sm',
  h6: 'heading-sm',
  p: 'body-md',
  span: 'body-md',
  label: 'label-md',
  code: 'code',
} as const

type VariantKey = keyof typeof variantElementMap
export type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'code'

interface TextProps extends VariantProps<typeof textVariants>, React.HTMLAttributes<HTMLElement> {
  as?: TextElement
}

export function Text({ variant, className, as, ...props }: TextProps) {
  const resolvedVariant =
    variant || (as && elementVariantMap[as as keyof typeof elementVariantMap]) || 'body-md'

  const Component = as || variantElementMap[resolvedVariant as VariantKey] || 'p'

  return React.createElement(Component, {
    className: cn(textVariants({ variant: resolvedVariant }), className),
    ...props,
  })
}
